import { useState, useCallback, useRef, useEffect, useMemo, useContext } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import InputChip from 'LuxKit/Chips/InputChip'
import AssistChip from 'LuxKit/Chips/AssistChip'
import BodyText from 'LuxKit/Typography/BodyText'
import LineSpinnerIcon from 'LuxKit/Icons/line/LineSpinnerIcon'
import LinePlusCircleIcon from 'LuxKit/Icons/line/LinePlusCircleIcon'
import LineFileIcon from 'LuxKit/Icons/line/LineFileIcon'
import LinePaperclipIcon from 'LuxKit/Icons/line/LinePaperclipIcon'
import LineCalendarPlusIcon from 'LuxKit/Icons/line/LineCalendarPlusIcon'
import LineSquareShapeIcon from 'LuxKit/Icons/line/LineSquareShapeIcon'
import LineXIcon from 'LuxKit/Icons/line/LineXIcon'
import SolidMicrophoneIcon from 'LuxKit/Icons/solid/SolidMicrophoneIcon'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import noop from 'lib/function/noop'
import { normaliseMessage } from 'tripPlanner/api/chatUtils'
import { trackVoiceInputUsed } from 'tripPlanner/utils/chatAnalytics'
import { useMicrophoneTranscriber } from './hooks/useMicrophoneTranscriber'
import RecordingOverlay from './RecordingOverlay'
import TextArea from 'components/Common/Form/Input/TextArea'
import ImageThumbnailPreview from './ImageThumbnailPreview'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import ChatInputShell from './ChatInputShell'
import MorphingSendButton from './MorphingSendButton'
import ModalContext from 'contexts/ModalContext'
import AddBookingModal from './AddBookingModal'

interface ChatMessageInputProps {
  onSubmit: (message: string) => void
  disabled?: boolean
  placeholder?: string
  value?: string
  onChange?: (value: string) => void
  disableSubmit?: boolean
  isStreaming?: boolean
  onStopStream?: () => void
  files?: Array<File>
  onFilesChange?: (files: Array<File>) => void
  selectedTags?: Array<string>
  onRemoveTag?: (tag: string) => void
  inputRef?: React.RefObject<HTMLTextAreaElement | null>
  hideDisclaimer?: boolean
  /** Optional chatId for analytics tracking */
  chatId?: number
}

const ACCEPT_EXTENSIONS = '.png,.jpeg,.jpg,.webp,.gif,.pdf,.csv,.txt'

const ACCEPTED_EXTENSIONS_LIST = ACCEPT_EXTENSIONS.split(',').map(ext => ext.toLowerCase().trim())

const isValidFileExtension = (fileName: string): boolean => {
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'))
  return ACCEPTED_EXTENSIONS_LIST.includes(extension)
}

const MAX_HEIGHT = 160
const ROWS = 1

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;
`

const FilePreviewItem = styled.div`
  position: relative;
`

const RemoveFileButton = styled.button`
  position: absolute;
  top: ${rem(-6)};
  right: ${rem(-6)};
  width: ${rem(20)};
  height: ${rem(20)};
  border-radius: 50%;
  border: none;
  background-color: ${props => props.theme.palette.neutral.default.one};
  color: ${props => props.theme.palette.neutral.default.eight};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  z-index: 1;

  &:hover {
    background-color: ${props => props.theme.palette.neutral.default.two};
  }
`

const FileCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${props => props.theme.palette.neutral.default.seven};
  border-radius: ${rem(8)};
  min-width: ${rem(160)};
  max-width: ${rem(240)};
`

const FileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(40)};
  height: ${rem(40)};
  background-color: ${props => props.theme.palette.neutral.default.six};
  border-radius: ${rem(8)};
  flex-shrink: 0;
  color: ${props => props.theme.palette.neutral.default.two};
`

const FileInfoWrapper = styled.div`
  min-width: 0;
  flex: 1;
`

const HiddenFileInput = styled.input`
  display: none;
`

const TextAreaWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  width: 100%;
  
  label,
  span {
    width: 100%;
    min-width: 0;
    border: none;
  }

  textarea {
    padding-left: 0;
  }
`

const AttachmentMenuAnchor = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`

const FloatingAttachmentMenu = styled.div`
  position: absolute;
  left: 0;
  bottom: calc(100% + ${rem(8)});
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(12)};
  z-index: 3;
`

const FloatingAssistChip = styled(AssistChip)`
  && {
    --chip-stroke-color: transparent;
    background-color: ${props => props.theme.palette.neutral.default.eight};
    box-shadow: 0 ${rem(6)} ${rem(18)} rgba(0, 0, 0, 0.14);
  }
`

function ChatMessageInput({
  onSubmit,
  disabled = false,
  placeholder,
  value: controlledValue,
  onChange = noop,
  disableSubmit = false,
  isStreaming = false,
  onStopStream = noop,
  files,
  onFilesChange,
  selectedTags,
  onRemoveTag = noop,
  inputRef,
  hideDisclaimer = false,
  chatId,
}: ChatMessageInputProps) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)

  const placeholderEmpty = intl.formatMessage({
    id: 'JreBnLG-TB3nexEa24pF',
    defaultMessage: 'Ask me anything',
    description: 'Placeholder text for empty chat input in TripPlanner',
  })
  const placeholderWithTags = intl.formatMessage({
    id: 'B1-3bpBEjRyHlrbaq_Qr',
    defaultMessage: 'Anything you’d like to add?',
    description: 'Placeholder text for chat input when tags are selected in TripPlanner',
  })
  const disclaimerText = intl.formatMessage({
    id: '2LcZhOVL1haWs_smYN68',
    defaultMessage: 'AI TripPlanner can make mistakes. Double-check important information.',
    description: 'AI accuracy disclaimer text displayed below the TripPlanner chat input',
  })
  const uploadPhotoOrFileChipLabel = intl.formatMessage({
    id: 'xFhyB-33Xsfg5y01N-wj',
    defaultMessage: 'Upload a photo or file',
    description: 'Assist chip label shown above TripPlanner chat input to open the existing file upload picker',
  })
  const addBookingChipLabel = intl.formatMessage({
    id: 'N3DmOda7OJ9ekDka-zc2',
    defaultMessage: 'Add a booking',
    description: 'Assist chip label shown above TripPlanner chat input for add booking action placeholder',
  })
  const [internalMessage, setInternalMessage] = useState('')
  const [isAttachmentOptionsVisible, setIsAttachmentOptionsVisible] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const attachmentMenuRef = useRef<HTMLDivElement>(null)
  const latestMessageRef = useRef('')
  const latestDisabledRef = useRef(false)
  const latestDisableSubmitRef = useRef(false)
  const shouldSubmitTranscriptionRef = useRef(false)

  // Sync external ref with internal ref
  useEffect(() => {
    if (inputRef && textareaRef.current) {
      inputRef.current = textareaRef.current
    }
  })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const message = controlledValue !== undefined ? controlledValue : internalMessage
  const hasMessage = Boolean(message.trim())
  const hasTags = Boolean(selectedTags?.length)
  const hasFiles = Boolean(files?.length)
  const hasContent = hasMessage || hasTags || hasFiles
  const placeholderText = placeholder ?? (hasTags ? placeholderWithTags : placeholderEmpty)

  latestMessageRef.current = message
  latestDisabledRef.current = disabled
  latestDisableSubmitRef.current = disableSubmit

  const resizeTextarea = useCallback(() => {
    if (!textareaRef.current) return
    const textarea = textareaRef.current
    textarea.style.height = 'auto'
    const newHeight = Math.min(textarea.scrollHeight, MAX_HEIGHT)
    textarea.style.height = `${newHeight}px`
    textarea.style.overflowY = textarea.scrollHeight > MAX_HEIGHT ? 'auto' : 'hidden'
  }, [])

  const appendTranscription = useCallback((text: string) => {
    const currentMessage = latestMessageRef.current
    const separator = currentMessage.trim() ? ' ' : ''
    const nextMessage = currentMessage + separator + text

    if (controlledValue !== undefined) {
      onChange(nextMessage)
    } else {
      setInternalMessage(nextMessage)
    }

    requestAnimationFrame(resizeTextarea)
    return nextMessage
  }, [controlledValue, onChange, resizeTextarea])

  const resetMessage = useCallback(() => {
    if (controlledValue !== undefined) {
      onChange('')
    } else {
      setInternalMessage('')
    }
  }, [controlledValue, onChange])

  const submitMessage = useCallback((nextMessage: string) => {
    if (disabled || disableSubmit) return false

    const processed = normaliseMessage(nextMessage)
    onSubmit(processed)
    resetMessage()
    requestAnimationFrame(resizeTextarea)
    textareaRef.current?.focus()
    return true
  }, [disableSubmit, disabled, onSubmit, resetMessage, resizeTextarea])

  const submitConfirmedTranscription = useCallback((nextMessage: string) => {
    if (submitMessage(nextMessage)) {
      return true
    }

    // Allow confirmed voice submit in controlled-empty-input edge case.
    const canSubmitAfterControlledUpdate = controlledValue !== undefined &&
      !latestDisabledRef.current &&
      latestDisableSubmitRef.current &&
      !normaliseMessage(controlledValue)

    if (!canSubmitAfterControlledUpdate) {
      return false
    }

    const processed = normaliseMessage(nextMessage)
    if (!processed) {
      return false
    }

    onSubmit(processed)
    resetMessage()
    requestAnimationFrame(resizeTextarea)
    textareaRef.current?.focus()
    return true
  }, [controlledValue, onSubmit, resetMessage, resizeTextarea, submitMessage])

  const handleTranscription = useCallback((text: string) => {
    if (shouldSubmitTranscriptionRef.current) {
      shouldSubmitTranscriptionRef.current = false
      const nextMessage = appendTranscription(text)
      submitConfirmedTranscription(nextMessage)
      return
    }

    appendTranscription(text)
  }, [appendTranscription, submitConfirmedTranscription])

  const handleAfterTranscription = useCallback(() => {
    shouldSubmitTranscriptionRef.current = false
  }, [])

  const {
    isRecording,
    isTranscribing,
    recordingDuration,
    waveformLevels,
    filledBars,
    startRecording,
    cancelRecording,
    submitRecording,
  } = useMicrophoneTranscriber({
    onTranscription: handleTranscription,
    onAfterTranscription: handleAfterTranscription,
  })

  useEffect(() => {
    resizeTextarea()
  }, [message, resizeTextarea])

  useEffect(() => {
    const stopContentScriptsFromFocus = (event: FocusEvent) => {
      if (textareaRef.current && event.target === textareaRef.current) {
        event.stopImmediatePropagation?.()
      }
    }

    document.addEventListener('focusin', stopContentScriptsFromFocus, true)
    return () => {
      document.removeEventListener('focusin', stopContentScriptsFromFocus, true)
    }
  }, [])

  useEffect(() => {
    if (!isAttachmentOptionsVisible) {
      return
    }

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      if (!(event.target instanceof Node)) {
        return
      }

      if (attachmentMenuRef.current?.contains(event.target)) {
        return
      }

      setIsAttachmentOptionsVisible(false)
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAttachmentOptionsVisible(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('touchstart', handlePointerDown)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('touchstart', handlePointerDown)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isAttachmentOptionsVisible])

  useEffect(() => {
    if (disabled || isTranscribing) {
      setIsAttachmentOptionsVisible(false)
    }
  }, [disabled, isTranscribing])

  const handleSubmit = useCallback(() => {
    submitMessage(message)
  }, [message, submitMessage])

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }, [handleSubmit])

  const handleChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event.target.value
    if (controlledValue !== undefined) {
      onChange(newValue)
    } else {
      setInternalMessage(newValue)
    }
    requestAnimationFrame(resizeTextarea)
  }, [controlledValue, onChange, resizeTextarea])

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || !onFilesChange) {
      return
    }

    const allFiles = Array.from(event.target.files)
    const validFiles: Array<File> = []
    const invalidFiles: Array<string> = []

    allFiles.forEach((file) => {
      if (isValidFileExtension(file.name)) {
        validFiles.push(file)
      } else {
        invalidFiles.push(file.name)
      }
    })

    if (invalidFiles.length > 0) {
      const fileNames = invalidFiles.join(', ')
      showSnackbar(
        intl.formatMessage(
          {
            id: 'MsYqYSByel_JeZ397Cjx',
            defaultMessage: '{count, plural, one {The following file cannot be uploaded} other {The following files cannot be uploaded}}: {fileNames}. Accepted formats: {formats}',
            description: 'Error message when files cannot be uploaded due to unsupported format',
          },
          { count: invalidFiles.length, fileNames, formats: ACCEPT_EXTENSIONS.replace(/\./g, '').toUpperCase() },
        ),
        'critical',
      )
    }

    if (validFiles.length > 0) {
      onFilesChange([...(files ?? []), ...validFiles])
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
    setIsAttachmentOptionsVisible(false)
  }, [intl, files, onFilesChange])

  const handleUploadClick = useCallback(() => {
    setIsAttachmentOptionsVisible((prev) => !prev)
  }, [])

  const handleUploadPhotoOrFileClick = useCallback(() => {
    setIsAttachmentOptionsVisible(false)
    fileInputRef.current?.click()
  }, [])

  const handleAddBookingClick = useCallback(() => {
    setIsAttachmentOptionsVisible(false)
    showModal(
      <AddBookingModal onSendMessage={onSubmit} />,
    )
  }, [onSubmit, showModal])

  const handleRemoveTag = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const tag = e.currentTarget.dataset.tag
    if (tag) onRemoveTag(tag)
  }, [onRemoveTag])

  const handleRemoveFile = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const index = e.currentTarget.dataset.index
    if (index === undefined || !files || !onFilesChange) return
    const fileIndex = parseInt(index, 10)
    const newFiles = files.filter((_, i) => i !== fileIndex)
    onFilesChange(newFiles)
  }, [files, onFilesChange])

  const handleRecordingCancel = useCallback(() => {
    shouldSubmitTranscriptionRef.current = false
    cancelRecording()
  }, [cancelRecording])

  const handleRecordingSubmit = useCallback(() => {
    trackVoiceInputUsed(chatId)
    shouldSubmitTranscriptionRef.current = true
    submitRecording()
  }, [submitRecording, chatId])

  const microphoneButton = useMemo(() => {
    if (isStreaming) return null

    return (
      <IconButton
        kind="tertiary"
        shape="circle"
        size="small"
        onClick={startRecording}
        disabled={disabled || isTranscribing}
      >
        {isTranscribing ? <LineSpinnerIcon /> : <SolidMicrophoneIcon />}
      </IconButton>
    )
  }, [disabled, isStreaming, isTranscribing, startRecording])

  const ctaButton = useMemo(() => {
    if (isStreaming) {
      return (
        <TextButton
          kind="secondary"
          variant="dark"
          size="medium"
          shape="round"
          onClick={onStopStream}
          endIcon={<LineSquareShapeIcon />}
        >
          {intl.formatMessage({
            id: '_1aP-MlD-tFxuTRBZ91f',
            defaultMessage: 'Stop',
            description: 'Action button label to stop AI response streaming in chat',
          })}
        </TextButton>
      )
    }

    return (
      <MorphingSendButton
        expanded={hasContent}
        onClick={handleSubmit}
        disabled={disabled || disableSubmit || !hasContent}
      />
    )
  }, [disabled, disableSubmit, handleSubmit, hasContent, intl, isStreaming, onStopStream])

  const shouldShowOverlay = isRecording || isTranscribing

  const recordingOverlay = useMemo(() => {
    if (!shouldShowOverlay) {
      return null
    }

    return (
      <RecordingOverlay
        placeholder={placeholderText}
        waveformLevels={waveformLevels}
        filledBars={filledBars}
        durationMs={recordingDuration}
        onCancel={handleRecordingCancel}
        onSubmit={handleRecordingSubmit}
        isTranscribing={isTranscribing}
      />
    )
  }, [
    filledBars,
    handleRecordingCancel,
    handleRecordingSubmit,
    isTranscribing,
    placeholderText,
    recordingDuration,
    shouldShowOverlay,
    waveformLevels,
  ])

  const chipsContent = useMemo(() => {
    if (!hasTags || !selectedTags) return null
    return (
      <Group direction="horizontal" gap={8} wrap="wrap">
        {selectedTags.map((tag, index) => (
          <InputChip
            key={`${tag}-${index}`}
            variant="filled"
            data-tag={tag}
            onClick={handleRemoveTag}
            aria-label={intl.formatMessage(
              {
                id: 'Zb2W-kUOdVBBetSY_w-p',
                defaultMessage: 'Remove {tag}',
                description: 'Aria-label for button to remove a selected tag from chat input',
              },
              { tag },
            )}
          >
            {tag}
          </InputChip>
        ))}
      </Group>
    )
  }, [hasTags, intl, selectedTags, handleRemoveTag])

  const filesContent = useMemo(() => {
    if (!files || files.length === 0) return null

    return (
      <Group direction="horizontal" gap={8} wrap="wrap">
        {files.map((file, index) => {
          const isImage = file.type.startsWith('image/')
          const fileExtension = file.name.split('.').pop()?.toUpperCase() || 'FILE'

          return (
            <FilePreviewItem key={`${file.name}-${index}`}>
              <RemoveFileButton
                data-index={index}
                onClick={handleRemoveFile}
                aria-label={intl.formatMessage(
                  {
                    id: 'e8YFBfyWPyJx0RyXqo69',
                    defaultMessage: 'Remove {fileName}',
                    description: 'Aria-label for button to remove an attached file from chat input',
                  },
                  { fileName: file.name },
                )}
              >
                <LineXIcon size="XS" />
              </RemoveFileButton>
              {isImage && <ImageThumbnailPreview file={file} />}
              {!isImage && (
                <FileCard>
                  <FileIconWrapper>
                    <LineFileIcon size="M" />
                  </FileIconWrapper>
                  <FileInfoWrapper>
                    <Group direction="vertical" gap={2}>
                      <BodyText variant="medium" weight="bold" colour="neutral-one" wrap="truncate" title={file.name}>
                        {file.name}
                      </BodyText>
                      <BodyText variant="small" colour="neutral-three" format="uppercase">
                        {fileExtension}
                      </BodyText>
                    </Group>
                  </FileInfoWrapper>
                </FileCard>
              )}
            </FilePreviewItem>
          )
        })}
      </Group>
    )
  }, [files, handleRemoveFile, intl])

  return (
    <Wrapper>
      <ChatInputShell disabled={disabled}>
        {chipsContent}
        {filesContent}
        <Group direction="horizontal" gap={8} verticalAlign="center" fullWidth>
          {onFilesChange && (
            <AttachmentMenuAnchor ref={attachmentMenuRef}>
              {isAttachmentOptionsVisible && (
                <FloatingAttachmentMenu>
                  <FloatingAssistChip
                    variant="filled"
                    size="large"
                    onClick={handleUploadPhotoOrFileClick}
                    startIcon={<LinePaperclipIcon size="S" />}
                  >
                    {uploadPhotoOrFileChipLabel}
                  </FloatingAssistChip>
                  <FloatingAssistChip
                    variant="filled"
                    size="large"
                    onClick={handleAddBookingClick}
                    startIcon={<LineCalendarPlusIcon size="S" />}
                  >
                    {addBookingChipLabel}
                  </FloatingAssistChip>
                </FloatingAttachmentMenu>
              )}
              <IconButton
                kind="tertiary"
                variant="dark"
                shape="circle"
                size="medium"
                horizontalOutdent="start"
                onClick={handleUploadClick}
                disabled={disabled || isTranscribing}
                aria-expanded={isAttachmentOptionsVisible}
                aria-label={intl.formatMessage({
                  id: 'V6LhP1J1ebLGBTDknit8',
                  defaultMessage: 'Attach files and more',
                  description: 'Aria-label for the attach files button in the chat message input',
                })}
              >
                <LinePlusCircleIcon />
              </IconButton>
            </AttachmentMenuAnchor>
          )}
          <TextAreaWrapper>
            <TextArea
              inputRef={textareaRef}
              placeholder={placeholderText}
              value={message}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              rows={ROWS}
              resize="none"
              noValidationSpacing
              autoFocus
            />
          </TextAreaWrapper>
          {microphoneButton}
          {ctaButton}
          {onFilesChange && (
            <HiddenFileInput
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              accept={ACCEPT_EXTENSIONS}
            />
          )}
          {recordingOverlay}
        </Group>
      </ChatInputShell>
      {!hideDisclaimer && (
        <BodyText variant="small" colour="neutral-three">
          {disclaimerText}
        </BodyText>
      )}
    </Wrapper>
  )
}

export default ChatMessageInput
